import React from 'react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
}

export const HojaCertificadoVGM: React.FC<Props> = ({ expediente }) => {
  const folioVGM = `VGM-ZLO-${expediente.folio.replace(/[^0-9]/g, '').slice(-5) || '99120'}`
  const pesoNeto = expediente.documentos.packingList.pesoNetoKg || 20250
  const tara = expediente.contenedor.taraKg || 4600
  const vgmTotal = pesoNeto + tara

  return (
    <div className="relative w-full bg-[#fcfcf9] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de Agua SOLAS */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.035] overflow-hidden">
        <div className="text-[110px] font-black uppercase text-slate-800 -rotate-25 select-none tracking-widest whitespace-nowrap">
          SOLAS VGM CERTIFIED
        </div>
      </div>

      {/* Encabezado Oficial IMO SOLAS */}
      <div className="border-b-2 border-slate-900 pb-3 mb-3 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-blue-950 text-white font-black text-sm flex items-center justify-center font-serif">
              ⚖
            </div>
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-600">
                INTERNATIONAL MARITIME ORGANIZATION (IMO) · SAFETY OF LIFE AT SEA
              </p>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-slate-950 uppercase font-mono">
                CERTIFICADO DE MASA BRUTA VERIFICADA (VGM)
              </h2>
              <p className="text-[8px] font-mono text-slate-500">
                CONVENIO INTERNACIONAL SOLAS · REGLA 2 DEL CAPÍTULO VI · DIRECTRIZ MSC.1/CIRC.1475
              </p>
            </div>
          </div>
        </div>

        <div className="text-right">
          <span className="font-mono text-xs font-black text-blue-900 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
            FOLIO: {folioVGM}
          </span>
          <p className="font-mono text-[8px] text-slate-500 mt-1">EMISIÓN: 04/OCT/2026</p>
          <span className="inline-block text-[7.5px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded mt-0.5">
            MÉTODO 1 (BÁSCULA CERTIFICADA)
          </span>
        </div>
      </div>

      {/* Cuadro de Identificación del Contenedor */}
      <div className="border-2 border-slate-900 mb-3 divide-y-2 divide-slate-900">
        <div className="grid grid-cols-12 divide-x divide-slate-900 bg-slate-100/70 font-mono text-[9px]">
          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">NÚMERO DE CONTENEDOR:</span>
            <span className="font-black text-sm text-slate-950">{expediente.contenedor.numero}</span>
            <span className="block text-[7.5px] text-slate-600">TIPO: {expediente.contenedor.tipo}</span>
          </div>
          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">LÍNEA NAVIERA:</span>
            <span className="font-bold text-xs text-slate-900">{expediente.contenedor.lineaNaviera}</span>
            <span className="block text-[7.5px] text-slate-600">BUQUE: {expediente.buque.nombre}</span>
          </div>
          <div className="col-span-4 p-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">PUERTO DE EMBARQUE:</span>
            <span className="font-bold text-xs text-slate-900">MANZANILLO, COLIMA (MXZLO)</span>
            <span className="block text-[7.5px] text-slate-600">TERMINAL: {expediente.buque.terminal}</span>
          </div>
        </div>

        {/* Datos del Embarcador / Shipper */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 p-2 font-mono text-[9px] bg-white">
          <div className="col-span-8 pr-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">
              EXPEDIDOR / EMBARCADOR RESPONSABLE (SHIPPER):
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.emisor.nombre}</p>
            <p className="text-[8px] text-slate-600">
              RFC / TAX ID: <strong className="text-slate-900">{expediente.actores.emisor.rfc}</strong> · DOMICILIO FISCAL
              REGISTRADO
            </p>
          </div>

          <div className="col-span-4 pl-2">
            <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">PRECINTOS / SELLOS:</span>
            <p className="font-bold text-slate-900 text-[9px]">{expediente.contenedor.sellos}</p>
            <span className="text-[7.5px] text-emerald-700 font-bold">PRECINTO DE ALTA SEGURIDAD ISO 17712</span>
          </div>
        </div>
      </div>

      {/* Desglose Matemático y Técnico del Pesaje */}
      <div className="border-2 border-slate-900 mb-3 bg-white">
        <div className="bg-slate-900 text-white px-2 py-0.5 font-bold text-[8.5px] uppercase tracking-wider flex justify-between">
          <span>DESGLOSE ANALÍTICO DE MASA BRUTA (KILOGRAMOS)</span>
          <span className="text-amber-300 font-mono text-[8px]">TOLERANCIA MÁXIMA PERMITIDA: ±500 KG</span>
        </div>

        <div className="p-3 space-y-2">
          <div className="grid grid-cols-3 gap-4 text-center font-mono">
            <div className="p-2 rounded bg-slate-50 border border-slate-200">
              <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">
                1. PESO NETO MERCANCÍA + EMBALAJE:
              </span>
              <span className="font-bold text-sm text-slate-900">{pesoNeto.toLocaleString()} KG</span>
              <p className="text-[7px] text-slate-500 mt-0.5">({(pesoNeto / 1000).toFixed(2)} TONELADAS)</p>
            </div>

            <div className="p-2 rounded bg-slate-50 border border-slate-200">
              <span className="block text-[7.5px] font-sans font-bold text-slate-500 uppercase">
                2. TARA DEL CONTENEDOR (VACÍO):
              </span>
              <span className="font-bold text-sm text-slate-900">{tara.toLocaleString()} KG</span>
              <p className="text-[7px] text-slate-500 mt-0.5">ESTAMPA DE PUERTA DEL CONTENEDOR</p>
            </div>

            <div className="p-2 rounded bg-blue-50 border border-blue-300">
              <span className="block text-[7.5px] font-sans font-bold text-blue-900 uppercase">
                3. MASA BRUTA VERIFICADA (VGM):
              </span>
              <span className="font-black text-base text-blue-950">{vgmTotal.toLocaleString()} KG</span>
              <p className="text-[7px] text-blue-700 font-bold mt-0.5">({(vgmTotal / 1000).toFixed(2)} TONELADAS)</p>
            </div>
          </div>

          <div className="p-2 rounded bg-emerald-50 border border-emerald-200 flex items-center justify-between text-[8px] font-mono">
            <div className="space-y-0.5">
              <span className="font-bold text-emerald-950 font-sans uppercase">
                CERTIFICACIÓN DE LA BÁSCULA DE PESAJE EN TERMINAL (SERVICIO BP1):
              </span>
              <p className="text-emerald-800">
                Báscula Plataforma Garita Terminal SSA México TEC I Manzanillo · Certificado de Calibración NOM-010-SCFI / CENAM No. ZLO-2026-B01.
              </p>
            </div>
            <span className="px-2 py-1 rounded bg-emerald-600 text-white font-black text-xs font-sans">
              SOLAS VERIFIED
            </span>
          </div>
        </div>
      </div>

      {/* Declaración Jurada y Firma Legal */}
      <div className="border-2 border-slate-900 p-3 bg-slate-50 font-mono text-[8px] relative">
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-8 text-slate-600 font-sans space-y-1">
            <p className="font-bold text-slate-900 text-[8.5px]">DECLARACIÓN JURADA DEL EMBARCADOR (SHIPPER):</p>
            <p className="leading-tight text-[7.5px]">
              "El abajo firmante certifica bajo protesta de decir verdad que la Masa Bruta Verificada (VGM) consignada en este documento ha sido obtenida de conformidad con las directrices del Convenio Internacional SOLAS y las leyes mercantiles y aduaneras mexicanas. Este contenedor no excede la capacidad de carga máxima (Max Gross Weight) autorizada en la placa CSC."
            </p>
          </div>

          <div className="col-span-4 border-l border-slate-300 pl-3 text-center">
            <div className="h-10 flex items-center justify-center">
              <span className="font-serif italic text-base text-blue-950 font-bold tracking-tight">
                Ing. Carlos Morales V.
              </span>
            </div>
            <div className="border-t border-slate-800 pt-0.5">
              <span className="font-sans font-bold text-[7.5px] uppercase text-slate-800 block">
                REPRESENTANTE LEGAL AUTORIZADO
              </span>
              <span className="text-[6.5px] text-slate-500 font-mono">EMISOR / EMBARCADOR SHIPPERS</span>
            </div>
          </div>
        </div>

        {/* Sello Redondo SOLAS */}
        <div className="absolute right-32 top-[-25px] pointer-events-none transform rotate-6">
          <div className="h-20 w-20 rounded-full border-2 border-dashed border-blue-900 bg-blue-50/80 backdrop-blur-sm p-1 flex flex-col items-center justify-center text-center text-blue-950 shadow-md">
            <span className="text-[6px] font-black uppercase tracking-wider">SOLAS COMPLIANT</span>
            <span className="text-[7.5px] font-black uppercase text-blue-900">VGM CERTIFIED</span>
            <span className="text-[5.5px] font-mono">IMO MSC.1/CIRC.1475</span>
          </div>
        </div>
      </div>

      <div className="mt-2 text-center text-[7px] font-mono text-slate-400">
        CERTIFICADO OBLIGATORIO PARA LA ESTIBA EN BUQUE DE CARGA SEGÚN LAS NORMAS INTERNACIONALES DE SEGURIDAD EN EL MAR.
      </div>
    </div>
  )
}
